import { Fragment, type ReactNode } from 'react';
import styled from 'styled-components';

/* Рендер Markdown ответа Эксперта: заголовки, абзацы, списки (с вложенностью),
   таблицы, цитаты, блоки кода, разделители, **жирный**, *курсив*, `код`,
   ссылки. Строит React-элементы — без innerHTML, поэтому разметка из ответа
   модели не исполняется. Ссылки — только http(s), mailto и страницы самой ОСНОВЫ
   (`/agents/…`) — они открываются в новой вкладке, чат остаётся на месте. */

const Root = styled.div`
  font-size: 16px;
  line-height: 1.7;
  overflow-wrap: anywhere;

  & > :first-child {
    margin-top: 0;
  }
  & > :last-child {
    margin-bottom: 0;
  }
  p {
    margin: 0 0 ${({ theme }) => theme.spacing(4)};
  }
  h1,
  h2,
  h3,
  h4 {
    margin: ${({ theme }) => `${theme.spacing(6)} 0 ${theme.spacing(3)}`};
    line-height: 1.3;
    font-weight: 700;
  }
  h1 {
    font-size: 24px;
  }
  h2 {
    font-size: 20px;
  }
  h3 {
    font-size: 18px;
  }
  h4 {
    font-size: 16px;
  }
  ul,
  ol {
    margin: 0 0 ${({ theme }) => theme.spacing(4)};
    padding-left: ${({ theme }) => theme.spacing(6)};
  }
  li {
    margin: ${({ theme }) => theme.spacing(1)} 0;
  }
  li > ul,
  li > ol {
    margin-bottom: 0;
  }
  blockquote {
    margin: 0 0 ${({ theme }) => theme.spacing(4)};
    padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
    border-left: 3px solid ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  code {
    font-family: ${({ theme }) => theme.font.mono};
    font-size: 14px;
    padding: 2px 6px;
    border-radius: ${({ theme }) => theme.radius.xs};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
  pre {
    margin: 0 0 ${({ theme }) => theme.spacing(4)};
    padding: ${({ theme }) => theme.spacing(4)};
    border-radius: ${({ theme }) => theme.radius.md};
    background: ${({ theme }) => theme.colors.surfaceHover};
    overflow-x: auto;
  }
  pre code {
    padding: 0;
    background: none;
    white-space: pre;
  }
  hr {
    border: 0;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    margin: ${({ theme }) => theme.spacing(4)} 0;
  }
  a {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin: 0 0 ${({ theme }) => theme.spacing(4)};

  table {
    border-collapse: collapse;
    font-size: 14.5px;
    line-height: 1.5;
    min-width: 50%;
  }
  th,
  td {
    border: 1px solid ${({ theme }) => theme.colors.border};
    padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
    text-align: left;
    vertical-align: top;
    /* Числа и даты не рвём посреди: узкой таблице — горизонтальная прокрутка. */
    overflow-wrap: normal;
    min-width: 64px;
  }
  th {
    background: ${({ theme }) => theme.colors.surfaceHover};
    font-weight: 600;
  }
`;

/* ── Строчная разметка ───────────────────────────────────────────────────── */

const INLINE =
  /(`[^`\n]+`)|(\*\*[^*\n]+?\*\*|__[^_\n]+?__)|(\*[^*\s][^*\n]*?\*|_[^_\s][^_\n]*?_)|(\[[^\]\n]+\]\([^)\s]+\))|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?»"'])/g;

function safeHref(url: string): string | null {
  if (/^(https?:\/\/|mailto:)/i.test(url)) return url;
  // Страница ОСНОВЫ: путь от корня, но не `//хост` (чужой сайт без схемы).
  return /^\/(?![\/\\])/.test(url) ? url : null;
}

export function renderInline(text: string, keyPrefix = 'i'): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const m of text.matchAll(INLINE)) {
    const start = m.index ?? 0;
    if (start > last) out.push(text.slice(last, start));
    const key = `${keyPrefix}${n++}`;
    const [whole, code, bold, italic, link, url] = m;
    if (code) {
      out.push(<code key={key}>{code.slice(1, -1)}</code>);
    } else if (bold) {
      out.push(<strong key={key}>{renderInline(bold.slice(2, -2), `${key}b`)}</strong>);
    } else if (italic) {
      out.push(<em key={key}>{renderInline(italic.slice(1, -1), `${key}e`)}</em>);
    } else if (link) {
      const close = link.indexOf('](');
      const label = link.slice(1, close);
      const href = safeHref(link.slice(close + 2, -1));
      out.push(
        href ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {renderInline(label, `${key}l`)}
          </a>
        ) : (
          <Fragment key={key}>{label}</Fragment>
        ),
      );
    } else if (url) {
      out.push(
        <a key={key} href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>,
      );
    } else {
      out.push(whole);
    }
    last = start + whole.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/* ── Блоки ───────────────────────────────────────────────────────────────── */

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'rule' }
  | { kind: 'table'; head: string[]; rows: string[][] }
  | { kind: 'list'; items: ListItem[]; ordered: boolean; start: number };

interface ListItem {
  text: string;
  children: Block[];
}

const LIST_RE = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const TABLE_SEP_RE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function cells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function indentOf(line: string): number {
  const m = /^(\s*)/.exec(line);
  return m ? m[1].replace(/\t/g, '    ').length : 0;
}

/** Разбор списка, начиная с `i`: пункты одного уровня + вложенные списки. */
function parseList(lines: string[], i: number): [Block, number] {
  const first = LIST_RE.exec(lines[i]);
  const baseIndent = indentOf(lines[i]);
  const ordered = !!first && /\d/.test(first[2]);
  const start = ordered && first ? parseInt(first[2], 10) || 1 : 1;
  const items: ListItem[] = [];
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      // Пустая строка внутри списка: продолжаем, если дальше снова пункт.
      const next = lines[i + 1];
      if (next !== undefined && LIST_RE.test(next) && indentOf(next) >= baseIndent) {
        i++;
        continue;
      }
      break;
    }
    const m = LIST_RE.exec(line);
    const ind = indentOf(line);
    if (m && ind === baseIndent) {
      items.push({ text: m[3], children: [] });
      i++;
    } else if (m && ind > baseIndent && items.length) {
      const [nested, next] = parseList(lines, i);
      items[items.length - 1].children.push(nested);
      i = next;
    } else if (!m && ind > baseIndent && items.length) {
      // Продолжение текста пункта.
      items[items.length - 1].text += ` ${line.trim()}`;
      i++;
    } else {
      break;
    }
  }
  return [{ kind: 'list', items, ordered, start }, i];
}

export function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) {
      i++;
      continue;
    }
    if (trimmed.startsWith('```')) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) body.push(lines[i++]);
      i++;
      blocks.push({ kind: 'code', text: body.join('\n') });
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (h) {
      blocks.push({ kind: 'heading', level: Math.min(h[1].length, 4), text: h[2].replace(/#+$/, '').trim() });
      i++;
      continue;
    }
    if (/^([-*_])(\s*\1){2,}$/.test(trimmed)) {
      blocks.push({ kind: 'rule' });
      i++;
      continue;
    }
    if (trimmed.startsWith('>')) {
      const q: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        q.push(lines[i].trim().replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({ kind: 'quote', lines: q });
      continue;
    }
    if (trimmed.includes('|') && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1])) {
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].trim().includes('|')) rows.push(cells(lines[i++]));
      blocks.push({ kind: 'table', head, rows });
      continue;
    }
    if (LIST_RE.test(line)) {
      const [list, next] = parseList(lines, i);
      blocks.push(list);
      i = next;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length) {
      const l = lines[i];
      const t = l.trim();
      if (
        !t ||
        t.startsWith('```') ||
        t.startsWith('>') ||
        /^#{1,6}\s/.test(t) ||
        LIST_RE.test(l) ||
        (t.includes('|') && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1]))
      ) {
        break;
      }
      para.push(t);
      i++;
    }
    blocks.push({ kind: 'paragraph', text: para.join('\n') });
  }
  return blocks;
}

function withBreaks(text: string, key: string): ReactNode[] {
  return text.split('\n').flatMap((part, idx) =>
    idx === 0 ? renderInline(part, `${key}-${idx}-`) : [<br key={`${key}br${idx}`} />, ...renderInline(part, `${key}-${idx}-`)],
  );
}

function renderBlock(b: Block, key: string): ReactNode {
  switch (b.kind) {
    case 'heading': {
      const content = renderInline(b.text, key);
      if (b.level === 1) return <h1 key={key}>{content}</h1>;
      if (b.level === 2) return <h2 key={key}>{content}</h2>;
      if (b.level === 3) return <h3 key={key}>{content}</h3>;
      return <h4 key={key}>{content}</h4>;
    }
    case 'paragraph':
      return <p key={key}>{withBreaks(b.text, key)}</p>;
    case 'code':
      return (
        <pre key={key}>
          <code>{b.text}</code>
        </pre>
      );
    case 'quote':
      return <blockquote key={key}>{withBreaks(b.lines.join('\n'), key)}</blockquote>;
    case 'rule':
      return <hr key={key} />;
    case 'table':
      return (
        <TableWrap key={key}>
          <table>
            <thead>
              <tr>
                {b.head.map((c, ci) => (
                  <th key={ci}>{renderInline(c, `${key}h${ci}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((r, ri) => (
                <tr key={ri}>
                  {b.head.map((_, ci) => (
                    <td key={ci}>{renderInline(r[ci] ?? '', `${key}r${ri}c${ci}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      );
    case 'list': {
      const items = b.items.map((it, idx) => (
        <li key={idx}>
          {renderInline(it.text, `${key}li${idx}`)}
          {it.children.map((c, ci) => renderBlock(c, `${key}li${idx}c${ci}`))}
        </li>
      ));
      return b.ordered ? (
        <ol key={key} start={b.start}>
          {items}
        </ol>
      ) : (
        <ul key={key}>{items}</ul>
      );
    }
  }
}

export function Markdown({ text }: { text: string }) {
  return <Root>{parseBlocks(text).map((b, i) => renderBlock(b, `b${i}`))}</Root>;
}
